import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChartBar, Path, Clock, CaretDown } from "@phosphor-icons/react";

/**
 * Compta widget — hours + km per technician for a selectable period.
 * Defaults to the current calendar month. Admin/associate/compta only
 * (backend enforces access; frontend just mounts inside the manager blockMap).
 */

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function ComptaHoursKmPanel() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get("/reports/compta/hours-km", { params: { date_from: from, date_to: to } })
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Erreur"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [from, to]);

  const totals = useMemo(() => ({
    hours: rows.reduce((a, r) => a + (r.hours || 0), 0),
    km: rows.reduce((a, r) => a + (r.km || 0), 0),
  }), [rows]);

  return (
    <div
      className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden"
      data-testid="compta-hours-km"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <ChartBar size={16} weight="duotone" className="text-emerald-300" />
          <span className="text-xs uppercase tracking-widest text-white/70 font-semibold">
            Compta · Heures & Km
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/40">
          <span className="flex items-center gap-1">
            <Clock size={11} className="text-emerald-300" weight="fill" />
            <span className="text-white/80 font-mono">{totals.hours.toFixed(1)}</span>h
          </span>
          <span className="flex items-center gap-1">
            <Path size={11} className="text-[#7CB2FF]" weight="fill" />
            <span className="text-white/80 font-mono">{totals.km.toFixed(0)}</span> km
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-white/40">
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
          data-testid="compta-from"
        />
        <CaretDown size={10} className="rotate-[-90deg] text-white/30" />
        <input
          type="date"
          value={to}
          min={from}
          max={today()}
          onChange={(e) => setTo(e.target.value)}
          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs"
          data-testid="compta-to"
        />
      </div>

      {loading ? (
        <div className="h-32 flex items-center justify-center text-white/40 text-xs">Chargement…</div>
      ) : error ? (
        <div className="h-24 flex items-center justify-center text-red-300/80 text-xs px-6 text-center">{error}</div>
      ) : rows.length === 0 ? (
        <div className="h-24 flex items-center justify-center text-white/40 text-xs px-6 text-center">
          Aucun technicien actif sur cette période.
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map((r) => {
            const hrsPct = totals.hours > 0 ? (r.hours / totals.hours) * 100 : 0;
            return (
              <div
                key={r.user_id}
                className="px-4 py-2.5 grid grid-cols-[1fr_auto_auto] items-center gap-4"
                data-testid={`compta-row-${r.user_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">{r.name}</div>
                  <div className="h-1 mt-1 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                      style={{ width: `${Math.min(100, hrsPct)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono tabular-nums text-emerald-300">{r.hours.toFixed(1)}</div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">heures</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono tabular-nums text-[#7CB2FF]">{r.km.toFixed(0)}</div>
                  <div className="text-[9px] uppercase tracking-widest text-white/40">km</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
